import { TabsContent } from "@/components/primitives/tabs";
import { usePaginatedMessageReactions } from "@/middleware/hooks/umbra/usePaginatedMessageReactions";
import { toast } from "@/hooks/useToast";
import { useEffect, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import ReactionListItem from "./ReactionListItem";

type Props = {
  chatId: string;
  messageId: string;
  emoji?: string;
  tabValue: string;
};

export default function ReactionsList({
  chatId,
  messageId,
  emoji,
  tabValue,
}: Props) {
  const parentRef = useRef<HTMLDivElement>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error,
    status,
  } = usePaginatedMessageReactions({
    chatId,
    messageId,
    emoji: emoji,
  });

  // `page.reactions` can be missing/undefined for a page (partial reactions
  // sync); `?? []` keeps flatMap from injecting an in-range `undefined` hole
  // into `results` (see #976).
  const results = !data
    ? []
    : data.pages.flatMap((page) => page.reactions ?? []);

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? results.length + 1 : results.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 52,
    overscan: 5,
  });

  useEffect(() => {
    if (error) {
      toast({
        duration: 1500,
        description:
          error instanceof Error ? error.message : "Cannot load members",
        variant: "destructive",
      });
    }
  }, [error]);

  useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

    if (!lastItem) {
      return;
    }

    if (
      lastItem.index >= results.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasNextPage,
    fetchNextPage,
    results.length,
    isFetchingNextPage,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    rowVirtualizer.getVirtualItems(),
  ]);

  return (
    <>
      <TabsContent
        key={emoji}
        value={tabValue}
        ref={parentRef}
        className="custom-scroll h-96"
        style={{
          // height: `100%`,
          width: `100%`,
          overflow: "auto",
        }}
      >
        {status === "pending" ? (
          <>
            <div className="flex items-center justify-start gap-3 truncate px-4 py-2 align-middle">
              <div className="size-10 shrink-0 animate-pulse rounded-full bg-muted" />
              <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
              <div className="size-5 shrink-0 animate-pulse rounded-sm bg-muted" />
            </div>
            <div className="flex items-center justify-start gap-3 truncate px-4 py-2 align-middle">
              <div className="size-10 shrink-0 animate-pulse rounded-full bg-muted" />
              <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
              <div className="size-5 shrink-0 animate-pulse rounded-sm bg-muted" />
            </div>
            <div className="flex items-center justify-start gap-3 truncate px-4 py-2 align-middle">
              <div className="size-10 shrink-0 animate-pulse rounded-full bg-muted" />
              <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
              <div className="size-5 shrink-0 animate-pulse rounded-sm bg-muted" />
            </div>
          </>
        ) : (
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const isLoaderRow = virtualRow.index > results.length - 1;
              const reactionUser = results[virtualRow.index];

              return (
                <div
                  key={virtualRow.index}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  {/* Guard both an in-range undefined hole AND a present-but-
                      partial row (the API response is an unvalidated `any` cast,
                      so `userId` can be missing at runtime): render the loader/
                      empty slot instead of crashing on reactionUser.reaction or
                      passing an undefined userId into UserAvatar (#976). */}
                  {isLoaderRow || !reactionUser?.userId ? (
                    hasNextPage && (
                      <>
                        <div className="flex items-center justify-start gap-3 truncate px-4 py-2 align-middle">
                          <div className="size-10 shrink-0 animate-pulse rounded-full bg-muted" />
                          <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
                          <div className="size-5 shrink-0 animate-pulse rounded-sm bg-muted" />
                        </div>
                        <div className="flex items-center justify-start gap-3 truncate px-4 py-2 align-middle">
                          <div className="size-10 shrink-0 animate-pulse rounded-full bg-muted" />
                          <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
                          <div className="size-5 shrink-0 animate-pulse rounded-sm bg-muted" />
                        </div>
                        <div className="flex items-center justify-start gap-3 truncate px-4 py-2 align-middle">
                          <div className="size-10 shrink-0 animate-pulse rounded-full bg-muted" />
                          <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
                          <div className="size-5 shrink-0 animate-pulse rounded-sm bg-muted" />
                        </div>
                      </>
                    )
                  ) : (
                    <ReactionListItem
                      chatId={chatId}
                      messageId={messageId}
                      reaction={reactionUser.reaction}
                      userId={reactionUser.userId}
                      reactionTimestamp={reactionUser.reactionTimestamp}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </TabsContent>
    </>
  );
}
